import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import createStyle from 'shared-utils/src/createStyle';
import makeClassName from 'shared-utils/src/makeClassName';
import styles, { mainCls, clsCtr, clsInput, clsCtrIndet, clsCtrIcon } from './styles';
import Grid from '../Grid';
import GridItem from '../GridItem';
import Check from 'shared-vectors/icons/Check';
import { Icon } from 'shared-vectors';

const useStyles = createStyle(styles);

export default function CheckboxField({
  className,
  name,
  value,
  label,
  color,
  onChange,
  checked,
  disabled,
  indeterminate,
  children,
  gutters,
  align,
  version,
}) {
  const classes = useStyles();
  const [chked, setChecked] = useState(checked);
  const [indet, setIndet] = useState(indeterminate);
  const elInput = useRef(null);
  useEffect(() => { setChecked(checked) }, [checked]);
  useEffect(() => { setIndet(indeterminate) }, [indeterminate]);
  useEffect(() => {
    const { current } = elInput;
    current.indeterminate = indet;
  }, [indet]);
  const handleChange = (ev) => {
    const { checked } =  ev.target;
    setChecked(checked);
    setIndet(false);
    onChange(checked, { value, name, event: ev });
  };
  const clsStr = makeClassName([
    classes[mainCls],
    version > 1 ? `${classes[mainCls]}--v${version}` : 0,
    className,
    'is-clickable',
  ]);

  const usableColor = color || 'primary';

  const clsCtrStr = makeClassName([
    classes[clsCtr],
    chked && `c-bg--${usableColor}`,
  ]);
  const labelRes = label || children;

  return (
    <label className={clsStr}>
        <Grid gutters={gutters} align={align}>
          <GridItem behavior="fixed">
            <input className={clsInput} ref={elInput} type="checkbox" name={name} onChange={handleChange} checked={chked} disabled={disabled} value={value}/>
            <div className={clsCtrStr}>
              <div className={`${classes[clsCtrIndet]} c-bg--${usableColor}`}/>
              <Icon name={Check} width="12px" color="w" className={`${classes[clsCtrIcon]}`} />
            </div>
          </GridItem>
          { !!labelRes && <GridItem>{labelRes}</GridItem> }
        </Grid>
    </label>
  );
};

CheckboxField.propTypes = {
  /**
   * Component class
   */
  className: PropTypes.string,

  /**
   * Input name
   */
  name: PropTypes.string,

  /**
   * Checkbox color
   */
  color: PropTypes.string,

  /**
   * Checkbox value
   */
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),

  /**
   * Checkbox label
   */
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),

  /**
   * Callback when the input is changed
   */
  onChange: PropTypes.func,

  /**
   * Initial checked state
   */
  checked: PropTypes.bool,

  /**
   * Initial indeterminate state
   */
  indeterminate: PropTypes.bool,

  /**
   * Disabled state
   */
  disabled: PropTypes.bool,

  /**
   * Grid gutters
   */
   gutters: PropTypes.string,

  /**
   * Grid align
   */
   align: PropTypes.string,   

   version: PropTypes.number,
};

CheckboxField.defaultProps = {
  className: '',
  color: 'primary',
  value: 0,
  label: '',
  name: null,
  checked: false,
  indeterminate: false,
  disabled: false,
  gutters: 'l',
  align: 'center',
  onChange: () => {},
  version: 1,
};
